import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { userApi, activityLogApi, llcApi } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Users, Shield, User, UserCheck, Pencil, Activity, LogIn, LogOut, AlertTriangle, ChevronDown, ChevronUp, Building2, Landmark, CreditCard, Send, Ban, FileText, Printer, Download } from 'lucide-react';
import { toast } from 'sonner';

export const UserManagementPage = () => {
  const { user: currentUser, startEmulation } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    allowed_llcs: [],
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    role: '',
    password: '',
    allowed_llcs: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [emulatingUserId, setEmulatingUserId] = useState(null);
  const [allLlcs, setAllLlcs] = useState([]);
  const [usersExpanded, setUsersExpanded] = useState(() => {
    const saved = localStorage.getItem('userMgmt_usersExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [logsExpanded, setLogsExpanded] = useState(() => {
    const saved = localStorage.getItem('userMgmt_logsExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const toggleUsers = () => {
    const next = !usersExpanded;
    setUsersExpanded(next);
    localStorage.setItem('userMgmt_usersExpanded', JSON.stringify(next));
  };

  const toggleLogs = () => {
    const next = !logsExpanded;
    setLogsExpanded(next);
    localStorage.setItem('userMgmt_logsExpanded', JSON.stringify(next));
  };

  useEffect(() => {
    fetchUsers();
    fetchActivityLogs();
    fetchLlcs();
  }, []);

  const fetchLlcs = async () => {
    try {
      const response = await llcApi.list();
      setAllLlcs(response.data);
    } catch (error) {
      console.error('Failed to fetch LLCs');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await userApi.list();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const response = await activityLogApi.list(50);
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleExportAuditLog = async () => {
    try {
      const response = await activityLogApi.exportCsv();
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `AuditLog_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit log exported');
    } catch (error) {
      toast.error('Failed to export audit log');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await userApi.create(formData);
      toast.success('User created successfully');
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'user', allowed_llcs: [] });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      password: '',
      allowed_llcs: user.allowed_llcs || [],
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const updateData = {
        name: editFormData.name,
        email: editFormData.email,
        role: editFormData.role,
        allowed_llcs: editFormData.role === 'clerk' ? editFormData.allowed_llcs : null,
      };
      // Only include password if it was changed
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }
      await userApi.update(editingUser.id, updateData);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await userApi.delete(userId);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEmulate = async (userId, userName) => {
    setEmulatingUserId(userId);
    try {
      const response = await userApi.emulate(userId);
      const { access_token, user: emulatedUser } = response.data;
      
      startEmulation(access_token, emulatedUser);
      toast.success(`Now viewing as ${userName}`, {
        description: 'Click "Stop Emulating" in the header to return to your account'
      });
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to emulate user');
    } finally {
      setEmulatingUserId(null);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'superadmin':
        return <Shield className="w-4 h-4 text-amber-600" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-600" />;
      case 'clerk':
        return <UserCheck className="w-4 h-4 text-green-600" />;
      default:
        return <User className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case 'login':
        return <LogIn className="w-4 h-4 text-green-600" />;
      case 'logout':
        return <LogOut className="w-4 h-4 text-blue-600" />;
      case 'login_failed':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'bank_created': case 'bank_updated': case 'bank_deleted':
        return <Landmark className="w-4 h-4 text-purple-600" />;
      case 'llc_created': case 'llc_updated': case 'llc_deleted':
        return <Building2 className="w-4 h-4 text-indigo-600" />;
      case 'payee_created': case 'payee_updated': case 'payee_deleted':
        return <Users className="w-4 h-4 text-teal-600" />;
      case 'check_created': case 'check_deleted':
        return <CreditCard className="w-4 h-4 text-emerald-600" />;
      case 'check_printed':
        return <Printer className="w-4 h-4 text-blue-600" />;
      case 'check_voided':
        return <Ban className="w-4 h-4 text-red-600" />;
      case 'check_pending':
        return <CreditCard className="w-4 h-4 text-amber-600" />;
      case 'wire_created': case 'wire_updated': case 'wire_deleted': case 'wire_sent': case 'wire_pending':
        return <Send className="w-4 h-4 text-orange-600" />;
      case 'data_purged':
        return <Trash2 className="w-4 h-4 text-red-600" />;
      default:
        return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActivityLabel = (action) => {
    const labels = {
      login: ['Login', 'green'],
      logout: ['Logout', 'blue'],
      login_failed: ['Failed Login', 'red'],
      bank_created: ['Bank Created', 'purple'],
      bank_updated: ['Bank Updated', 'purple'],
      bank_deleted: ['Bank Deleted', 'red'],
      llc_created: ['LLC Created', 'indigo'],
      llc_updated: ['LLC Updated', 'indigo'],
      llc_deleted: ['LLC Deleted', 'red'],
      payee_created: ['Payee Created', 'teal'],
      payee_updated: ['Payee Updated', 'teal'],
      payee_deleted: ['Payee Deleted', 'red'],
      check_created: ['Check Created', 'emerald'],
      check_printed: ['Check Printed', 'blue'],
      check_voided: ['Check Voided', 'red'],
      check_pending: ['Check Pending', 'amber'],
      check_deleted: ['Check Deleted', 'red'],
      wire_created: ['Wire Created', 'orange'],
      wire_updated: ['Wire Updated', 'orange'],
      wire_deleted: ['Wire Deleted', 'red'],
      wire_sent: ['Wire Sent', 'green'],
      wire_pending: ['Wire Pending', 'amber'],
      data_purged: ['Data Purged', 'red'],
    };
    const [label, color] = labels[action] || [action, 'slate'];
    return <span className={`text-${color}-700 bg-${color}-50 px-2 py-0.5 rounded text-xs font-medium`}>{label}</span>;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (currentUser?.role !== 'superadmin') {
    return (
      <div>
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="w-12 h-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900">Access Denied</h3>
            <p className="text-slate-500 mt-1">Only superadmins can manage users</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3 fade-in" data-testid="user-management-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">User Management</h1>
          <p className="text-slate-500 mt-1">Manage user accounts and permissions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="user-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  data-testid="user-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  data-testid="user-password-input"
                />
              </div>

              <div>
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(val) => setFormData(prev => ({ ...prev, role: val, allowed_llcs: val !== 'clerk' ? [] : prev.allowed_llcs }))}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clerk">Clerk</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="superadmin">Superadmin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.role === 'clerk' && (
                <div>
                  <Label>Assigned LLCs</Label>
                  <p className="text-xs text-slate-500 mb-2">Select which LLCs this clerk can access</p>
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1" data-testid="llc-select-create">
                    {allLlcs.map(llc => (
                      <label key={llc.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={formData.allowed_llcs.includes(llc.id)}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              allowed_llcs: e.target.checked
                                ? [...prev.allowed_llcs, llc.id]
                                : prev.allowed_llcs.filter(id => id !== llc.id)
                            }));
                          }}
                          className="rounded"
                        />
                        <span>{llc.company_name}</span>
                      </label>
                    ))}
                    {allLlcs.length === 0 && <p className="text-xs text-slate-400 p-2">No LLCs found</p>}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="user-submit-btn">
                  {submitting ? 'Creating...' : 'Create User'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                name="name"
                value={editFormData.name}
                onChange={handleEditInputChange}
                required
                data-testid="edit-user-name-input"
              />
            </div>

            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                name="email"
                type="email"
                value={editFormData.email}
                onChange={handleEditInputChange}
                required
                data-testid="edit-user-email-input"
              />
            </div>

            <div>
              <Label htmlFor="edit-role">Role</Label>
              <Select value={editFormData.role} onValueChange={(val) => setEditFormData(prev => ({ ...prev, role: val, allowed_llcs: val !== 'clerk' ? [] : prev.allowed_llcs }))}>
                <SelectTrigger data-testid="edit-user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clerk">Clerk</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="superadmin">Superadmin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editFormData.role === 'clerk' && (
              <div>
                <Label>Assigned LLCs</Label>
                <p className="text-xs text-slate-500 mb-2">Select which LLCs this clerk can access</p>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1" data-testid="llc-select-edit">
                  {allLlcs.map(llc => (
                    <label key={llc.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={editFormData.allowed_llcs.includes(llc.id)}
                        onChange={(e) => {
                          setEditFormData(prev => ({
                            ...prev,
                            allowed_llcs: e.target.checked
                              ? [...prev.allowed_llcs, llc.id]
                              : prev.allowed_llcs.filter(id => id !== llc.id)
                          }));
                        }}
                        className="rounded"
                      />
                      <span>{llc.company_name}</span>
                    </label>
                  ))}
                  {allLlcs.length === 0 && <p className="text-xs text-slate-400 p-2">No LLCs found</p>}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="edit-password">New Password (leave blank to keep current)</Label>
              <Input
                id="edit-password"
                name="password"
                type="password"
                value={editFormData.password}
                onChange={handleEditInputChange}
                placeholder="Leave blank to keep current password"
                minLength={6}
                data-testid="edit-user-password-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="edit-user-submit-btn">
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="border-slate-200">
        <CardHeader className="cursor-pointer select-none rounded-t-lg panel-header-themed"  onClick={toggleUsers} data-testid="users-panel-toggle">
          <CardTitle className="flex items-center justify-between" style={{ color: '#ffffff' }}>
            <span className="flex items-center gap-2" style={{ color: '#ffffff' }}>
              <Users className="w-5 h-5" />
              All Users
            </span>
            {usersExpanded ? <ChevronUp className="w-5 h-5 text-slate-300" /> : <ChevronDown className="w-5 h-5 text-slate-300" />}
          </CardTitle>
        </CardHeader>
        {usersExpanded && (
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading users...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Created</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td className="font-medium">{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          {getRoleIcon(user.role)}
                          <span className="capitalize">{user.role}</span>
                        </div>
                        {user.role === 'clerk' && user.allowed_llcs?.length > 0 && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            {user.allowed_llcs.map(lid => allLlcs.find(l => l.id === lid)?.company_name || lid.slice(0, 8)).join(', ')}
                          </div>
                        )}
                        {user.role === 'clerk' && (!user.allowed_llcs || user.allowed_llcs.length === 0) && (
                          <div className="text-xs text-amber-500 mt-0.5">No LLCs assigned</div>
                        )}
                      </td>
                      <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {user.id !== currentUser.id && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-slate-600 hover:text-slate-700"
                                onClick={() => handleEdit(user)}
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={() => handleEmulate(user.id, user.name)}
                                disabled={emulatingUserId === user.id}
                                data-testid={`emulate-user-${user.id}`}
                              >
                                <UserCheck className="w-4 h-4 mr-1" />
                                {emulatingUserId === user.id ? '...' : 'Emulate'}
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid={`delete-user-${user.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete User?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete {user.name}'s account.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDelete(user.id)} className="bg-red-600 hover:bg-red-700">
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                          {user.id === currentUser.id && (
                            <span className="text-xs text-slate-400">Current user</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        )}
      </Card>
      {/* Activity Log Card */}
      <Card className="border-slate-200">
        <CardHeader className="cursor-pointer select-none rounded-t-lg panel-header-themed"  onClick={toggleLogs} data-testid="logs-panel-toggle">
          <CardTitle className="flex items-center justify-between" style={{ color: '#ffffff' }}>
            <span className="flex items-center gap-2" style={{ color: '#ffffff' }}>
              <Activity className="w-5 h-5" />
              User Activity Log
            </span>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-transparent border-slate-500 text-white hover:bg-slate-700 h-7 text-xs"
                onClick={(e) => { e.stopPropagation(); handleExportAuditLog(); }}
                data-testid="export-audit-log-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Export CSV
              </Button>
              {logsExpanded ? <ChevronUp className="w-5 h-5 text-slate-300" /> : <ChevronDown className="w-5 h-5 text-slate-300" />}
            </div>
          </CardTitle>
        </CardHeader>
        {logsExpanded && (
        <CardContent>
          {logsLoading ? (
            <div className="text-center py-8 text-slate-500">Loading activity logs...</div>
          ) : activityLogs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No activity logs yet</div>
          ) : (
            <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
              <table className="data-table">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr>
                    <th>Action</th>
                    <th>Details</th>
                    <th>User</th>
                    <th>IP Address</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {activityLogs.map(log => (
                    <tr key={log.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          {getActivityIcon(log.action)}
                          {getActivityLabel(log.action)}
                        </div>
                      </td>
                      <td className="text-sm text-slate-700 max-w-xs truncate" title={log.details || ''}>{log.details || '—'}</td>
                      <td className="font-medium text-sm">{log.user_name}<br/><span className="text-xs text-slate-500">{log.user_email}</span></td>
                      <td>
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">
                          {log.ip_address}
                        </code>
                      </td>
                      <td className="text-sm text-slate-600">{formatDate(log.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        )}
      </Card>
    </div>
  );
};
